/**
 * Map Preloader - Preloads Mapbox resources for faster map rendering
 */
class MapPreloader {
  constructor() {
    this.preloadedResources = new Set();
    this.isPreloading = false;
    this.mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
  }

  /**
   * Preload essential Mapbox resources
   */
  async preloadMapResources() {
    if (this.isPreloading || !this.mapboxToken) return;
    
    this.isPreloading = true;
    console.log('🗺️ Preloading Mapbox resources...');

    try {
      // Preload the map style
      await this.preloadMapStyle();
      
      // Preload common map tiles for the area
      await this.preloadCommonTiles();
      
      console.log('✅ Mapbox resources preloaded');
    } catch (error) {
      console.warn('⚠️ Failed to preload some map resources:', error);
    } finally {
      this.isPreloading = false;
    }
  }

  /**
   * Preload the map style
   */
  async preloadMapStyle() {
    const styleUrl = `https://api.mapbox.com/styles/v1/mapbox/streets-v11?access_token=${this.mapboxToken}`;
    
    if (this.preloadedResources.has(styleUrl)) return;
    
    try {
      const response = await fetch(styleUrl);
      if (response.ok) {
        this.preloadedResources.add(styleUrl);
        console.log('✅ Map style preloaded');
      }
    } catch (error) {
      console.warn('⚠️ Failed to preload map style:', error);
    }
  }

  /**
   * Preload common map tiles for the area
   */
  async preloadCommonTiles() {
    // Preload tiles for common areas (San Francisco, etc.)
    const commonAreas = [
      { lat: 37.7749, lng: -122.4194, zoom: 10 }, // San Francisco
      { lat: 37.3382, lng: -121.8863, zoom: 10 }, // San Jose
      { lat: 37.8715, lng: -122.2730, zoom: 10 }, // Berkeley
    ];

    const tilePromises = commonAreas.map(area => 
      this.preloadTile(area.lat, area.lng, area.zoom)
    );

    await Promise.allSettled(tilePromises);
  }

  /**
   * Preload a specific tile
   */
  async preloadTile(lat, lng, zoom) {
    const tileX = Math.floor((lng + 180) / 360 * Math.pow(2, zoom));
    const tileY = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom));
    
    const tileUrl = `https://api.mapbox.com/v4/mapbox.streets/${zoom}/${tileX}/${tileY}@2x.png?access_token=${this.mapboxToken}`;
    
    if (this.preloadedResources.has(tileUrl)) return;
    
    try {
      const response = await fetch(tileUrl);
      if (response.ok) {
        this.preloadedResources.add(tileUrl);
      }
    } catch (error) {
      // Silently fail for tile preloading
    }
  }

  /**
   * Check if resources are preloaded
   */
  isPreloaded() {
    return this.preloadedResources.size > 0;
  }

  /**
   * Get preload statistics
   */
  getStats() {
    return {
      preloadedResources: this.preloadedResources.size,
      isPreloading: this.isPreloading
    };
  }
}

const mapPreloader = new MapPreloader();
export default mapPreloader;
